import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Link2, Trophy, Pencil, StickyNote, Cloud, Tag, Star } from 'lucide-react';
import { useState } from 'react';
import { GradeRevealAnimation } from '../components/GradeRevealAnimation';

export default function DemoActivities() {
  const navigate = useNavigate();
  const [showGradeDemo, setShowGradeDemo] = useState(false);

  const activities = [
    {
      id: 'matching-pairs',
      title: 'Matching Pairs',
      description: 'Drag and drop to match items in two columns',
      icon: Link2,
      color: 'from-blue-500 to-cyan-500',
      path: '/demo/matching-pairs',
    },
    {
      id: 'time-to-climb',
      title: 'Time to Climb',
      description: 'Gamified quiz with real-time leaderboard',
      icon: Trophy,
      color: 'from-orange-500 to-red-500',
      path: '/demo/time-to-climb',
    },
    {
      id: 'quiz',
      title: 'Quiz',
      description: 'Multi-question quiz with navigation and review',
      icon: CheckCircle,
      color: 'from-blue-500 to-indigo-500',
      path: '/demo/quiz',
    },
    {
      id: 'draw-it',
      title: 'Draw-It',
      description: 'Canvas drawing tool for visual responses',
      icon: Pencil,
      color: 'from-purple-500 to-pink-500',
      path: '/demo/draw-it',
    },
    {
      id: 'collaborate-board',
      title: 'Collaborate Board',
      description: 'Digital sticky notes for group brainstorming',
      icon: StickyNote,
      color: 'from-green-500 to-emerald-500',
      path: '/demo/collaborate-board',
    },
    {
      id: 'word-cloud',
      title: 'Word Cloud',
      description: 'Live word cloud visualization from student input',
      icon: Cloud,
      color: 'from-indigo-500 to-blue-500',
      path: '/demo/word-cloud',
    },
    {
      id: 'label-diagram',
      title: 'Label Diagram',
      description: 'Drag labels to correct positions on a diagram',
      icon: Tag,
      color: 'from-teal-500 to-green-500',
      path: '/demo/label-diagram',
    },
    {
      id: 'grade-reveal',
      title: 'Grade Reveal Animation',
      description: 'Real-time grading feedback with confetti',
      icon: Star,
      color: 'from-amber-500 to-yellow-500',
      action: () => setShowGradeDemo(true),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Interactive Activity Demos
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Explore the various interactive activities available in EduLive
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {activities.map((activity) => (
            <div
              key={activity.id}
              onClick={() => activity.action ? activity.action() : navigate(activity.path)}
              className="group bg-card rounded-3xl border-2 border-border overflow-hidden hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2 cursor-pointer"
            >
              <div className={`h-32 bg-gradient-to-br ${activity.color} flex items-center justify-center relative overflow-hidden`}>
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                <activity.icon className="w-16 h-16 text-white relative z-10" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{activity.title}</h3>
                <p className="text-muted-foreground mb-4">{activity.description}</p>
                <Button className="w-full rounded-xl" variant="outline">
                  Try Demo
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-card rounded-3xl p-8 border border-border inline-block">
            <p className="text-muted-foreground mb-4">Ready to start teaching?</p>
            <Button size="lg" onClick={() => navigate('/teacher/dashboard')} className="rounded-xl">
              Go to Teacher Dashboard
            </Button>
          </div>
        </div>
      </div>

      {showGradeDemo && (
        <GradeRevealAnimation 
          score={18} 
          maxScore={20} 
          onClose={() => setShowGradeDemo(false)} 
        />
      )}
    </div>
  );
}